
import { GoogleGenerativeAI, Content, Part } from "@google/generative-ai";
import { Message, Role, User } from '../types';
import { SYSTEM_INSTRUCTION } from '../constants';
import { optimizeHistory } from './optimizationService';
import {
  processAttachmentsIntelligently,
  createOptimizedAttachmentPrompt,
  shouldPreAnalyze,
  EnhancedAttachmentContext
} from './enhancedAttachmentHandler';
import { detectComplexity, detectRequiredModel, estimateOutputTokens } from '../utils/complexityDetector';
import { TokenChargeRequest, chargeTokens, refundTokens } from './token';
import { connectionManager } from '../utils/connectionManager';
import { geminiRateLimiter, RateLimitError } from './rateLimiter';
import { getCountryKnowledgeBase, getCountrySystemPrompt } from '../config/countryKnowledgeBases';
import { generatePersonaPrompt, analyzeUserProfile, PersonaMode } from './personaEngine';
import { generateCPASystemInstruction, requiresCPAProtocol } from '../config/cpaTopnotcherProtocol';
import { generateComprehensiveExamInstruction, requiresComprehensiveProtocol } from '../config/comprehensiveExamProtocol';
import { TABLE_GENERATION_RULES, validateTableStructure } from '../config/tableGenerationProtocol';
import { MOBILE_FRIENDLY_FORMATS } from '../config/mobileTableAlternatives';
import { aiLearningService } from './aiLearningService';
import { collectTrainingData } from './trainingDataService';

/**
 * Legacy Rate Limiter Interface
 * 
 * @deprecated Use geminiRateLimiter from './rateLimiter' directly.
 * This wrapper exists for backward compatibility only.
 */
const rateLimiter = {
  canMakeRequest(estimatedTokens: number = 0): { allowed: boolean; reason?: string; waitTime?: number } {
    if (!geminiRateLimiter.canRequestNow()) {
      const waitTime = geminiRateLimiter.getWaitTime();
      const stats = geminiRateLimiter.getStats();

      if (stats.dailyLimitReached) {
        return {
          allowed: false,
          reason: 'Daily request limit reached. Resets at midnight.',
          waitTime
        };
      }

      return {
        allowed: false,
        reason: 'Rate limit exceeded. Please wait.',
        waitTime
      };
    }
    return { allowed: true };
  },

  recordRequest(tokensUsed: number = 0): void {
    // The unified rate limiter records automatically
    console.log(`[RateLimiter] Request tracked via unified service`);
  },

  getUsageStats() {
    const stats = geminiRateLimiter.getStats();
    return {
      rpm: stats.requestsThisMinute,
      rpd: stats.requestsToday,
      tpm: 0, // Token tracking handled separately
      rpmLimit: 15,
      rpdLimit: 1500,
      tpmLimit: 1000000
    };
  }
};

// Export rate limiter for backward compatibility
export { rateLimiter };

/**
 * Request Queue using the unified rate limiter.
 */
class RequestQueue {
  async add<T>(task: () => Promise<T>, priority: 'high' | 'normal' | 'low' = 'normal'): Promise<T> {
    try {
      return await geminiRateLimiter.execute(task, { priority });
    } catch (error) {
      if (error instanceof RateLimitError) {
        // Convert to user-friendly error
        throw new Error(`Rate limit: ${error.message}. Try again in ${Math.ceil(error.retryAfter / 1000)}s.`);
      }
      throw error;
    }
  }
}

// Simple LRU Cache
class ResponseCache {
  private cache: Map<string, any>;
  private maxSize: number;

  constructor(maxSize: number = 50) {
    this.cache = new Map();
    this.maxSize = maxSize;
  }

  get(key: string): any | undefined {
    const item = this.cache.get(key);
    if (item) {
      // Refresh key
      this.cache.delete(key);
      this.cache.set(key, item);
    }
    return item;
  }

  set(key: string, value: any): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, value);
  }

  generateKey(messages: any[], model: string): string {
    return `${model}:${JSON.stringify(messages.slice(-3))}`; // Cache based on last 3 messages + model
  }
}

const requestQueue = new RequestQueue();
const responseCache = new ResponseCache();

export interface AIOptions {
  mode?: 'standard' | 'educator' | 'lawyer';
  depth?: 'concise' | 'balanced' | 'detailed' | 'comprehensive';
  autoBudget?: boolean;
  budgetPerRequest?: number;
}

const getClient = () => {
  // Default API key (always available as fallback)
  const DEFAULT_API_KEY = 'AIzaSyDfI0ey0RYiNcQm9LBXRoJgP5rbPah6FtI';

  // Try to get API key from localStorage config first
  let apiKey = '';
  try {
    const savedConfig = localStorage.getItem('ledger_config');
    if (savedConfig) {
      const config = JSON.parse(savedConfig);
      apiKey = config.apiKey || '';
    }
  } catch (error) {
    console.warn("Error reading config from localStorage:", error);
  }

  // Use saved API key if available, otherwise use default
  if (!apiKey) {
    apiKey = DEFAULT_API_KEY;
  }

  return new GoogleGenerativeAI(apiKey);
};

// Model hierarchy - optimized for Free Tier rate limits
// See: https://ai.google.dev/gemini-api/docs/rate-limits#tier-1
// Gemini 2.0 Flash: 15 RPM, 1M TPM, 200 RPD (Best free tier value!)
// Gemini 2.5 Flash-Lite: Latest economy model with enhanced reasoning
// Gemini 2.5 Flash: Full reasoning model for complex problems
// Gemini 3 Flash Preview: God Mode (Ultimate fallback for Max/Premium users)
const MODEL_HIERARCHY = [
  { name: 'gemini-2.5-flash-lite', tier: 1, tokensMultiplier: 0.5, description: 'Economy Pro' },
  { name: 'gemini-2.0-flash-lite', tier: 2, tokensMultiplier: 0.5, description: 'Economy' },
  { name: 'gemini-2.0-flash', tier: 3, tokensMultiplier: 1, description: 'Standard' },
  { name: 'gemini-2.5-flash', tier: 4, tokensMultiplier: 1.5, description: 'Advanced Reasoning' },
  { name: 'gemini-3-flash-preview', tier: 5, tokensMultiplier: 2.0, description: 'God Mode (Preview)' }
];

// Detect if query requires advanced model - ENHANCED for CPALE computational problems
export const requiresAdvancedModel = (message: string): boolean => {
  const complexityIndicators = [
    // Complex calculations
    /consolidat(e|ion)/i,
    /liquidat(e|ion)/i,
    /variance.*analysis/i,
    /multiple.*step/i,
    /complex/i,
    /compute/i,
    /calculate/i,
    /amortiz(e|ation)/i,
    /reconcil(e|iation)/i,
    /depreciat(e|ion)/i,

    // Advanced topics
    /business.*combination/i,
    /foreign.*currency.*translation/i,
    /derivative/i,
    /hedge.*accounting/i,
    /share.*based.*payment/i,
    /deferred.*tax/i,
    /lease/i,
    /pfrs/i,
    /pas\s\d/i,

    // Output requirements
    /journal.*entry/i,
    /adjusting.*entry/i,
    /balance.*sheet/i,
    /income.*statement/i,
    /cash.*flow/i,

    // Multiple concepts
    /and.*also/i,
    /both.*and/i,
    /compare.*contrast/i,

    // Uncertainty phrases
    /not.*sure/i,
    /confused/i,
    /difficult/i,
    /challenging/i,
    /help.*understand/i,

    // Detailed analysis
    /explain.*detail/i,
    /step.*by.*step/i,
    /show.*work/i,
    /prove/i,
    /justify/i,

    // ============ NEW: CPALE Computational Problem Patterns ============

    // Donor's Tax / Estate Tax / Transfer Taxes
    /donor('s)?.*tax/i,
    /estate.*tax/i,
    /donation(s)?/i,
    /donee/i,
    /gift(s)?.*worth/i,
    /gross.*gift/i,
    /net.*gift/i,
    /stranger.*relative/i,

    // Residency/Situs Rules
    /resident.*citizen/i,
    /non-resident.*alien/i,
    /resident.*alien/i,
    /situs/i,
    /reciprocity/i,
    /philippine-situs/i,

    // Multi-item scenarios (CPALE word problems)
    /following.*donations/i,
    /following.*transfers/i,
    /following.*properties/i,
    /following.*transactions/i,
    /worth.*P?\d+/i,        // "worth P450,000"
    /amounting.*to/i,
    /mortgaged.*for/i,
    /assumed.*by/i,
    /condoned/i,

    // Question numbering (indicates CPALE-style exam questions)
    /___?\d+\./i,           // "___21." or "__21."
    /problem\s+\d+/i,       // "Problem 1"
    /question\s+\d+/i,      // "Question 5"

    // Tax-specific computations
    /taxable.*income/i,
    /tax.*liability/i,
    /tax.*due/i,
    /tax.*payable/i,
    /withholding.*tax/i,
    /final.*tax/i,
    /capital.*gains.*tax/i,
    /value.*added.*tax/i,
    /percentage.*tax/i,

    // FMV/SP scenarios
    /fmv/i,                 // Fair Market Value
    /fair.*market.*value/i,
    /selling.*price/i,
    /sp/i,                  // If in context of FMV/SP comparison

    // Complex scenarios
    /if.*she.*is/i,         // "If she is a non-resident alien"
    /if.*he.*is/i,
    /if.*they.*are/i,
    /assuming/i,
    /given.*that/i,

    // Numerical complexity (many numbers = complex problem)
    /P?\s*\d{1,3}(,\d{3})+/g,  // Philippine peso amounts like "P1,000,000"
  ];

  // Check for complexity indicators
  const matchCount = complexityIndicators.filter(pattern => pattern.test(message)).length;

  // Also check for numerical density (many amounts = complex computation)
  const amountPattern = /P?\s*\d{1,3}(,\d{3})+|\d+,\d+/g;
  const amounts = message.match(amountPattern);
  const hasMultipleAmounts = amounts && amounts.length >= 3;

  // Also check for bullet/list patterns (indicates multiple items to process)
  const bulletPattern = /[•\-\*]\s+[A-Z]/g;
  const bullets = message.match(bulletPattern);
  const hasMultipleBullets = bullets && bullets.length >= 3;

  // Trigger escalation if:
  // 1. Any complexity indicator matches AND message is long (>200 chars), OR
  // 2. Multiple amounts are present, OR  
  // 3. Multiple bullet points with complex content
  return (matchCount > 0 && message.length > 200) || hasMultipleAmounts || hasMultipleBullets || matchCount >= 3;
};

// Get next tier model with safe fallback
const getNextTierModel = (currentModel: string): string | null => {
  if (!currentModel) return MODEL_HIERARCHY[0].name; // Fallback to base
  const currentIndex = MODEL_HIERARCHY.findIndex(m => m.name === currentModel);
  if (currentIndex === -1) {
    console.warn(`Model '${currentModel}' not found in hierarchy. Falling back to base.`);
    return MODEL_HIERARCHY[0].name;
  }
  if (currentIndex === MODEL_HIERARCHY.length - 1) return null;
  return MODEL_HIERARCHY[currentIndex + 1].name;
};

// Get model multiplier with safe fallback
const getModelMultiplier = (modelName: string): number => {
  const model = MODEL_HIERARCHY.find(m => m.name === modelName);
  return model?.tokensMultiplier || 1;
};

/**
 * Detect the academic domain from a user's message for targeted learning injection.
 */
function detectDomainFromMessage(message: string): string {
  const lower = message.toLowerCase();

  // Taxation
  if (/\b(tax|nirc|bir|vat|rcit|mcit|create law|eopt|withholding|donor.?s?\s*tax|estate\s*tax|income\s*tax|percentage\s*tax|capital\s*gains|final\s*tax|creditable|exemption|deduction)\b/i.test(lower)) return 'TAX';

  // Auditing
  if (/\b(audit|psa\s*\d|assurance|internal\s*control|substantive|analytical\s*procedure|risk\s*assessment|materiality|engagement\s*letter|audit\s*report|going\s*concern|key\s*audit\s*matter|kam)\b/i.test(lower)) return 'AUD';

  // Advanced Accounting
  if (/\b(consolidat|business\s*combination|partnership\s*liquidat|foreign\s*currency|nci|non-?controlling|goodwill|pfrs\s*10|pfrs\s*3|joint\s*venture|fund\s*accounting|government\s*accounting|safe\s*payment|cash\s*priority)\b/i.test(lower)) return 'AFAR';

  // Financial Accounting
  if (/\b(pfrs|pas\s*\d|depreciat|amortiz|impairment|receivable|inventory|ppe|property\s*plant|intangible|lease|revenue\s*recognition|financial\s*instrument|fair\s*value|revaluation|provision|contingent|equity\s*method|investment\s*property)\b/i.test(lower)) return 'FAR';

  // Management Advisory Services
  if (/\b(cvp|break.?even|variance|standard\s*cost|budget|capital\s*budget|npv|irr|payback|wacc|transfer\s*pric|activity.?based|abc\s*costing|relevant\s*cost|make\s*or\s*buy|special\s*order)\b/i.test(lower)) return 'MAS';

  // Regulatory Framework / Business Law
  if (/\b(contract|obligation|civil\s*code|corporation|rcc|negotiable\s*instrument|agency|partnership\s*law|trust|insurance\s*code|intellectual\s*property|data\s*privacy|amla|labor\s*code|article\s*\d|g\.?r\.?\s*no|supreme\s*court|irac|legal\s*analysis)\b/i.test(lower)) return 'RFBT';

  return 'GENERAL';
}

/**
 * Detect if a response was cut off before completion.
 * Uses both the Gemini API finishReason and heuristic text analysis.
 */
function detectCutOffResponse(text: string, finishReason: string | null): boolean {
  // API-level detection: MAX_TOKENS means the model hit its output limit
  if (finishReason === 'MAX_TOKENS' || finishReason === 'RECITATION') {
    return true;
  }

  // If finishReason is STOP (normal completion) or SAFETY, don't flag as cut-off
  if (finishReason === 'STOP' || finishReason === 'SAFETY') {
    return false;
  }

  // Heuristic detection for when finishReason is unavailable
  if (!text || text.length < 100) return false;

  const trimmed = text.trimEnd();

  // Check if response ends mid-sentence (no terminal punctuation)
  const lastChar = trimmed.slice(-1);
  const endsWithTerminal = /[.!?:)\]}`'"*\n]/.test(lastChar);

  // Check for incomplete patterns
  const incompletePatterns = [
    /\d+\.\s*$/,                    // Ends with a numbered list item start "3. "
    /[-•]\s*$/,                     // Ends with a bullet point start
    /\|\s*$/,                       // Ends mid-table row
    /\*\*[^*]+$/,                   // Ends with unclosed bold
    /```[^`]*$/,                    // Ends inside a code block
    /\|\s*[-:]+\s*\|\s*$/,         // Ends with table separator row
    /(?:Step|STEP)\s+\d+/,         // Check if there's a step pattern
  ];

  // If text is long (>2000 chars) and doesn't end with terminal punctuation
  if (trimmed.length > 2000 && !endsWithTerminal) {
    return true;
  }

  // Check for incomplete patterns
  for (const pattern of incompletePatterns) {
    if (pattern.test(trimmed.slice(-50))) {
      return true;
    }
  }

  // Check for numbered list that seems to stop abruptly
  // e.g., has "1." and "2." but response ends without completing the list
  const numberedItems = trimmed.match(/^\d+\.\s/gm);
  if (numberedItems && numberedItems.length >= 2) {
    const lastNumber = parseInt(numberedItems[numberedItems.length - 1]);
    // If the last 200 chars contain a reference to a higher number, it was cut off
    const tail = trimmed.slice(-200);
    const higherRef = tail.match(/(\d+)\s+(?:items?|steps?|points?|reasons?)/i);
    if (higherRef && parseInt(higherRef[1]) > lastNumber) {
      return true;
    }
  }

  return false;
}

// ═══════════════════════════════════════════════════════════════════════════
// TIERED SYSTEM INSTRUCTION BUILDER v1.0
// Reduces system prompt tokens by 50-90% based on query complexity.
//
// Tier 1 (SIMPLE):   ~2K tokens — greetings, basic definitions, short factual
// Tier 2 (STANDARD): ~6K tokens — typical CPA/review questions
// Tier 3 (COMPLEX):  ~12K tokens — multi-step computations, exam problems
// Tier 4 (EXPERT):   ~20K tokens — full protocol (attachments, exam protocols)
// ═══════════════════════════════════════════════════════════════════════════

type PromptComplexityTier = 1 | 2 | 3 | 4;

interface TieredPromptResult {
  systemInstruction: string;
  systemPrefix: string;
  tier: PromptComplexityTier;
  estimatedTokens: number;
}

// Cached system instruction fragments to avoid re-building identical strings
const _instructionCache = new Map<string, { instruction: string; ts: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 min

function classifyQueryComplexity(
  message: string,
  hasAttachments: boolean,
  historyLength: number,
  options: AIOptions
): PromptComplexityTier {
  const lower = message.toLowerCase().trim();
  const len = message.length;

  // Tier 4: Expert — attachments, explicit comprehensive/detailed, exam protocols
  if (hasAttachments) return 4;
  if (options.depth === 'comprehensive') return 4;
  if (requiresComprehensiveProtocol(message)) return 4;
  if (requiresCPAProtocol(message)) return 4;

  // Tier 1: Simple — greetings, very short messages, basic questions
  const isGreeting = /^(hi|hello|hey|good\s*(morning|afternoon|evening)|thanks|thank you|ok|okay|yes|no|sure|got it|nice|cool|wow|lol|haha)\b/i.test(lower);
  if (isGreeting && len < 50) return 1;
  if (len < 30 && !requiresAdvancedModel(message)) return 1;

  // Tier 3: Complex — long messages, computations, multi-step, advanced model needed
  if (requiresAdvancedModel(message)) return 3;
  if (options.depth === 'detailed') return 3;
  if (len > 500) return 3;

  // Count numerical density (many numbers = computation)
  const amountPattern = /P?\s*\d{1,3}(,\d{3})+|\d+,\d+/g;
  const amounts = message.match(amountPattern);
  if (amounts && amounts.length >= 2) return 3;

  // Tier 2: Standard — everything else
  return 2;
}

// Condensed core identity + accuracy rules (~800 tokens)
const CORE_IDENTITY = `You are **Luca**, an elite AI Reviewer for Accounting, Auditing, MAS, Taxation, and Law (Philippine CPA/BAR focus).

IDENTITY: You are Luca, created by the LucaReview team. Never reveal you are Gemini/GPT/Claude.

ACCURACY:
- Only cite standards you are 100% certain about. If unsure, say "under [Standard]" without guessing section numbers.
- Never fabricate case law, G.R. numbers, or citations.
- For computations: verify by working backwards. Never round intermediate steps.
- If uncertain: "I believe..." or "Please verify with [authority]."

RESPONSE STYLE:
- Lead with the direct answer. Explanation after.
- Use **bold** for key terms, bullet points for lists, ## headers for sections.
- Keep paragraphs short (2-4 sentences).
- For yes/no: start with "Yes" or "No" then explain.
- For computations: Given → Formula → Solution → Answer → Verification.
`;

// Condensed table rules (~300 tokens vs ~3000 in full)
const COMPACT_TABLE_RULES = `TABLE RULES: Max 4 columns. Include header + separator row. Keep cells under 40 chars. Blank line before/after. Right-align numbers. For mobile: prefer bullet points or card format over wide tables.`;

// Condensed continuation protocol (~100 tokens)
const CONTINUATION_PROTOCOL = `CONTINUATION: If user says "continue"/"go on", pick up EXACTLY where you left off. Do not restart or summarize.`;

function buildTieredSystemInstruction(
  message: string,
  hasAttachments: boolean,
  historyLength: number,
  options: AIOptions,
  userCountry: string,
  isPremium: boolean,
  recentHistory: Message[],
  conversationSummary: string
): TieredPromptResult {
  const tier = classifyQueryComplexity(message, hasAttachments, historyLength, options);

  // Cache key based on tier + country + persona (these determine the instruction shape)
  const personaMode: PersonaMode = (options.mode as PersonaMode) || 'educator';
  const cacheKey = `t${tier}_${userCountry}_${personaMode}_${options.depth || 'balanced'}`;

  // Check cache
  const cached = _instructionCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < CACHE_TTL) {
    // Still need to append conversation summary (dynamic per request)
    const finalInstruction = conversationSummary
      ? `${cached.instruction}\n\n${conversationSummary}`
      : cached.instruction;
    return {
      systemInstruction: finalInstruction,
      systemPrefix: '', // Already baked into instruction
      tier,
      estimatedTokens: Math.ceil(finalInstruction.length / 4)
    };
  }

  // ── Build instruction based on tier ──
  let instruction = '';

  // ── TIER 1: Simple (~2K tokens) ──
  if (tier === 1) {
    instruction = `${CORE_IDENTITY}
${COMPACT_TABLE_RULES}
${CONTINUATION_PROTOCOL}

Current Date: ${new Date().toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })}
User Location: ${userCountry}
Currency: ₱ (Philippine Peso)

Be concise. For simple greetings, respond warmly and briefly.`;
  }

  // ── TIER 2: Standard (~6K tokens) ──
  else if (tier === 2) {
    const countryKnowledge = getCountryKnowledgeBase(userCountry);

    // Persona prompt (condensed)
    const userProfile = analyzeUserProfile(
      recentHistory.slice(-4).map(m => ({ role: m.role === Role.USER ? 'user' : 'assistant', content: m.content })),
      message
    );
    const personaPrompt = generatePersonaPrompt(personaMode, userProfile);

    instruction = `${CORE_IDENTITY}

${personaPrompt}

THOUGHT PROCESS: For questions involving calculations, legal analysis, or multi-part problems, use <thought>...</thought> tags for internal reasoning before your visible response. Skip for simple factual questions.

${COMPACT_TABLE_RULES}
${CONTINUATION_PROTOCOL}

Current Date: ${new Date().toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })}
User Location: ${userCountry}
Currency: ${countryKnowledge?.currency.symbol || '₱'} (${countryKnowledge?.currency.name || 'Philippine Peso'})
Tax Authority: ${countryKnowledge?.taxation.authority || 'BIR'}
Accounting Framework: ${countryKnowledge?.accounting.framework || 'PFRS/PAS'}

Default to ${userCountry} laws and standards unless user specifies otherwise. Use local currency in examples.`;

    // Depth guidance
    if (options.depth === 'concise') {
      instruction += `\n\nCONCISE MODE: Brief, direct answers only. No elaboration unless asked.`;
    }
  }

  // ── TIER 3: Complex (~12K tokens) ──
  else if (tier === 3) {
    const countryKnowledge = getCountryKnowledgeBase(userCountry);
    const countryPromptAddition = getCountrySystemPrompt(userCountry);

    const userProfile = analyzeUserProfile(
      recentHistory.slice(-6).map(m => ({ role: m.role === Role.USER ? 'user' : 'assistant', content: m.content })),
      message
    );
    const personaPrompt = generatePersonaPrompt(personaMode, userProfile);

    instruction = `${CORE_IDENTITY}

${personaPrompt}

THOUGHT PROCESS RULES:
You MUST use <thought>...</thought> tags for internal reasoning when:
- Any calculation or numerical problem
- Multiple parts or sub-questions
- Legal analysis or case application
- Comparing alternatives or making judgments
- Ambiguous questions

Inside <thought> tags:
1. Restate what is being asked
2. List every piece of given data
3. Identify the specific standard/law/formula
4. Work through step by step
5. Check: Does it make sense? Right formula? Missed anything?
6. Correct errors BEFORE writing visible response

CONTEXT AWARENESS:
- Read conversation history carefully for follow-ups
- If user references "the problem above", look back in conversation
- Track what user already knows to avoid re-explaining

${COMPACT_TABLE_RULES}

MOBILE: 40% of users are on mobile. Default to 2-column max tables or use bullet/card format instead.

${CONTINUATION_PROTOCOL}

Current Date: ${new Date().toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })}
User Location: ${userCountry}
Currency: ${countryKnowledge?.currency.symbol || '₱'} (${countryKnowledge?.currency.name || 'Philippine Peso'})

${countryPromptAddition}

Tax Authority: ${countryKnowledge?.taxation.authority || 'BIR'}
Accounting Framework: ${countryKnowledge?.accounting.framework || 'PFRS/PAS'}
Corporate Tax Rate: ${countryKnowledge?.taxation.corporateIncomeTax.standardRate || '25%'}

Default to ${userCountry} laws. Cite specific provisions when certain. Use local currency.
If unsure about a rate or provision: "Please verify with current ${countryKnowledge?.taxation.authority || 'tax authority'} guidance."`;

    // Depth
    if (options.depth === 'detailed') {
      instruction += `\n\nDETAILED MODE: Thorough explanation with examples, steps, and context.`;
    } else if (options.depth === 'concise') {
      instruction += `\n\nCONCISE MODE: Brief, direct answers only.`;
    }

    // Auto-budget
    if (options.autoBudget && options.budgetPerRequest) {
      const suggestedChars = Math.floor(options.budgetPerRequest * 4);
      instruction += `\n\nBUDGET GUIDELINE: User prefers ~${suggestedChars} chars. Be efficient but NEVER cut off responses. Complete all answers fully.`;
    }
  }

  // ── TIER 4: Expert — Full protocol (~20K tokens) ──
  else {
    // This is the full system instruction for maximum quality on complex/exam/attachment queries
    const countryKnowledge = getCountryKnowledgeBase(userCountry);
    const countryPromptAddition = getCountrySystemPrompt(userCountry);

    const userProfile = analyzeUserProfile(
      recentHistory.slice(-8).map(m => ({ role: m.role === Role.USER ? 'user' : 'assistant', content: m.content })),
      message
    );
    const personaPrompt = generatePersonaPrompt(personaMode, userProfile);

    // Exam enhancement (only for tier 4)
    let examEnhancement = '';
    if (requiresComprehensiveProtocol(message)) {
      examEnhancement = '\n\n' + generateComprehensiveExamInstruction(message) + '\n';
    } else if (requiresCPAProtocol(message)) {
      examEnhancement = '\n\n' + generateCPASystemInstruction(message) + '\n';
    }

    // Use the full SYSTEM_INSTRUCTION from constants for tier 4
    instruction = `${SYSTEM_INSTRUCTION}

${personaPrompt}

CRITICAL REASONING DIRECTIVES:

<THOUGHT_PROCESS_RULES>
You MUST use <thought>...</thought> tags for internal reasoning when:
- Any calculation or numerical problem
- Multiple parts or sub-questions
- Legal analysis or case application
- Comparing alternatives or making judgments
- Ambiguous questions

Inside <thought> tags:
1. Restate what is being asked
2. List every piece of given data
3. Identify the specific standard/law/formula
4. Work through step by step
5. Check: Does it make sense? Right formula? Missed anything?
6. Correct errors BEFORE writing visible response

For simple greetings or basic factual questions, skip <thought> tags.
</THOUGHT_PROCESS_RULES>

<ACCURACY_RULES>
- Cite standards only when certain. If unsure, say "under the applicable standard."
- Double-check tax rates, thresholds, deadlines against training data.
- For calculations: verify by working backwards or alternative method.
- Never round intermediate steps.
- If question contains an error, point it out.
</ACCURACY_RULES>

<CONTEXT_AWARENESS>
- Read ENTIRE conversation history. User may be following up.
- If user references "the problem above", look back.
- Don't repeat previous answers verbatim — add new insight.
- Track what user already knows.
</CONTEXT_AWARENESS>

${CONTINUATION_PROTOCOL}

${TABLE_GENERATION_RULES}

${MOBILE_FRIENDLY_FORMATS}

${examEnhancement}

Current Date: ${new Date().toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })}
User Location: ${userCountry}
Currency: ${countryKnowledge?.currency.symbol || '₱'} (${countryKnowledge?.currency.name || 'Philippine Peso'})

${countryPromptAddition}

Tax Authority: ${countryKnowledge?.taxation.authority || 'BIR'}
Accounting Framework: ${countryKnowledge?.accounting.framework || 'PFRS/PAS'}
Corporate Tax Rate: ${countryKnowledge?.taxation.corporateIncomeTax.standardRate || '25%'}

Default to ${userCountry} laws. Cite specific provisions. Use local currency.
If unsure: "Please verify with current ${countryKnowledge?.taxation.authority || 'tax authority'} guidance."`;

    // Depth
    if (options.depth === 'concise') {
      instruction += `\n\nCONCISE MODE: Brief, direct answers only.`;
    } else if (options.depth === 'detailed') {
      instruction += `\n\nDETAILED MODE: Thorough explanation with examples, steps, and context.`;
    } else if (options.depth === 'comprehensive') {
      instruction += `\n\nCOMPREHENSIVE MODE: Complete analysis with citations, examples, edge cases, and nuances.`;
    }

    // Auto-budget
    if (options.autoBudget && options.budgetPerRequest) {
      const suggestedChars = Math.floor(options.budgetPerRequest * 4);
      instruction += `\n\nBUDGET GUIDELINE: ~${suggestedChars} chars preferred. NEVER cut off responses. Complete all answers fully.`;
    }
  }

  // Cache the instruction (without conversation summary which is dynamic)
  _instructionCache.set(cacheKey, { instruction, ts: Date.now() });

  // Append conversation summary
  const finalInstruction = conversationSummary
    ? `${instruction}\n\n${conversationSummary}`
    : instruction;

  const estimatedTokens = Math.ceil(finalInstruction.length / 4);
  console.log(`[PromptOptimizer] Tier ${tier} selected — ~${estimatedTokens} system tokens (message: ${message.length} chars)`);

  return {
    systemInstruction: finalInstruction,
    systemPrefix: '', // Everything is in the instruction now
    tier,
    estimatedTokens
  };
}

export async function* sendMessageToGeminiStream(
  history: Message[],
  currentMessage: string,
  attachments: { mimeType: string; data: string }[] = [],
  modelName: string = 'gemini-2.5-flash-lite',
  isPremium: boolean = false,
  allowModelEscalation: boolean = true,
  options: AIOptions = {},
  userCountry: string = 'Philippines',
  signal?: AbortSignal,
  user?: User,
  updateUser?: (updates: Partial<User>) => void
): AsyncGenerator<{ text: string; thought?: string; modelUsed?: string; tokensMultiplier?: number; tokensCharged?: number }> {
  // Check rate limits FIRST before doing anything
  const estimatedTokens = Math.ceil(currentMessage.length / 4) + (attachments.length * 1000);
  const rateCheck = rateLimiter.canMakeRequest(estimatedTokens);

  if (!rateCheck.allowed) {
    console.warn('[Gemini] Rate limited:', rateCheck.reason);
    yield {
      text: `⚠️ **Rate Limit**: ${rateCheck.reason}\n\nPlease wait ${Math.ceil((rateCheck.waitTime || 0) / 1000)} seconds before trying again.`,
      thought: "Rate limit protection triggered"
    };
    return;
  }

  // Stream cannot be easily queued in the same way because it yields. 
  // However, we can await a queue slot before starting the generator.
  // Add a timeout to prevent deadlock if the queue is saturated
  try {
    await Promise.race([
      requestQueue.add(async () => { return; }),
      new Promise((_, reject) => setTimeout(() => reject(new Error('QUEUE_TIMEOUT')), 15000))
    ]);
  } catch (queueError: any) {
    if (queueError.message === 'QUEUE_TIMEOUT') {
      console.warn('[Gemini] Queue slot timed out, proceeding anyway');
    }
  }

  // Record this request for rate limiting
  rateLimiter.recordRequest(estimatedTokens);

  // Adaptive timeout for initial connection only (cleared once streaming starts)
  const TIMEOUT_MS = connectionManager.getAdaptiveTimeout(isPremium);
  console.log(`[Gemini] Using adaptive timeout: ${TIMEOUT_MS}ms`);

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('REQUEST_TIMEOUT'));
    }, TIMEOUT_MS);
  });

  // Validate model name - convert invalid/preview models to default
  const VALID_MODELS = MODEL_HIERARCHY.map(m => m.name);
  const DEFAULT_MODEL = 'gemini-2.5-flash-lite';

  let validatedModel = modelName;
  if (!modelName || modelName.includes('preview') || modelName.includes('exp') || !VALID_MODELS.includes(modelName)) {
    console.warn(`[Gemini] Invalid model "${modelName}" → using ${DEFAULT_MODEL}`);
    validatedModel = DEFAULT_MODEL;
  }

  console.log(`Starting AI request (Stream) - Model: ${validatedModel}, Premium: ${isPremium}, Options:`, options);
  const ai = getClient();

  if (!ai) {
    yield {
      text: "⚠️ **System Error**: API Key is missing. Please configure your Gemini API Key in the Admin Dashboard → Settings.",
      thought: "Configuration Error: API Key is not configured."
    };
    return;
  }

  // CENTRALIZED TOKEN SYSTEM: Detect complexity and calculate charge
  let tokensCharged = 0;
  let chargeSuccessful = false;

  if (user && updateUser) {
    const complexityAnalysis = detectComplexity(currentMessage, attachments.length > 0);
    const recommendedModel = detectRequiredModel(complexityAnalysis.level, isPremium);

    // Calculate attachment size
    const attachmentSizeKB = attachments.reduce((sum, att) => sum + (att.data.length / 1024), 0);

    const tokenRequest: TokenChargeRequest = {
      operationType: 'chat',
      complexity: complexityAnalysis.level,
      model: recommendedModel,
      features: {
        hasAttachments: attachments.length > 0,
        attachmentCount: attachments.length,
        attachmentSizeKB: Math.ceil(attachmentSizeKB),
        hasWhiteboard: false, // Will be detected later
        hasThoughtProcess: options.mode === 'educator',
        usesFollowUpContext: false,
        preAnalyzed: false,
      },
      estimatedInputTokens: currentMessage.length / 4,
      estimatedOutputTokens: estimateOutputTokens(complexityAnalysis.level, attachments.length > 0),
    };

    try {
      const chargeResult = await chargeTokens(tokenRequest, user, updateUser);

      if (!chargeResult.canProceed) {
        yield {
          text: `⚠️ **Insufficient Tokens**: ${chargeResult.warnings.join('. ')}

You have ${chargeResult.remainingTokens} tokens remaining. This request requires ${chargeResult.charged} tokens.`,
          thought: "Token limit exceeded"
        };
        return;
      }

      tokensCharged = chargeResult.charged;
      chargeSuccessful = true;
      console.log(`[TokenSystem] Charged ${tokensCharged} tokens for ${complexityAnalysis.level} ${tokenRequest.operationType}`);
    } catch (error) {
      console.error('[TokenSystem] Failed to charge tokens:', error);
      // Continue anyway for now, but log the error
    }
  }

  // Context Management: Dynamic history with conversation summary
  const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isCapacitorApp = !!(window as any).Capacitor;

  // Pre-classify complexity to adjust history window (saves tokens for simple queries)
  const earlyTier = classifyQueryComplexity(currentMessage, attachments.length > 0, history.length, options);

  // Recent window: full messages the AI sees verbatim
  // Summary window: older messages compressed into a summary for context retention
  let RECENT_WINDOW: number;
  let SUMMARY_WINDOW: number;

  if (earlyTier === 1) {
    // Simple queries (greetings, short factual) need minimal context
    RECENT_WINDOW = 2;
    SUMMARY_WINDOW = 0;
  } else if (isMobileDevice || isCapacitorApp) {
    RECENT_WINDOW = isPremium ? 8 : 5;
    SUMMARY_WINDOW = isPremium ? 12 : 8;
  } else {
    RECENT_WINDOW = isPremium ? 14 : 8;
    SUMMARY_WINDOW = isPremium ? 20 : 12;
  }

  const validHistory = history.filter(msg => msg.role !== Role.SYSTEM && !msg.isError);
  const recentHistory = validHistory.slice(-RECENT_WINDOW);
  const olderHistory = validHistory.slice(0, Math.max(0, validHistory.length - RECENT_WINDOW)).slice(-SUMMARY_WINDOW);

  // Generate conversation summary from older messages to preserve context
  let conversationSummary = '';
  if (olderHistory.length >= 2) {
    const summaryParts: string[] = [];
    for (const msg of olderHistory) {
      const role = msg.role === Role.USER ? 'User' : 'Luca';
      // Extract key content: first 120 chars of each message
      const snippet = msg.content.replace(/[#*`\n]+/g, ' ').trim().substring(0, 120);
      if (snippet) summaryParts.push(`${role}: ${snippet}`);
    }
    if (summaryParts.length > 0) {
      conversationSummary = `[CONVERSATION CONTEXT - Earlier in this chat, the following was discussed:\n${summaryParts.join('\n')}\n— Use this context to maintain continuity. Do not repeat information already covered unless asked.]\n\n`;
    }
  }

  if (isMobileDevice || isCapacitorApp) {
    console.log(`[Context] Mobile: ${RECENT_WINDOW} recent + ${olderHistory.length} summarized messages`);
  }

  let contents: Content[] = recentHistory
    .map((msg, idx) => {
      let contentText = msg.content;

      // For mobile, truncate messages older than last 3 to save data
      const isRecent = idx >= recentHistory.length - 3;
      if ((isMobileDevice || isCapacitorApp) && !isRecent && contentText.length > 800) {
        contentText = contentText.substring(0, 800) + '...[truncated]';
      }

      // Include attachment metadata in history for context continuity
      if (msg.attachments && msg.attachments.length > 0) {
        const attachmentInfo = msg.attachments.map(att => `[Attached: ${att.name}]`).join(' ');
        contentText = `${attachmentInfo}\n${contentText}`;
      }
      return {
        role: msg.role === Role.USER ? 'user' : 'model',
        parts: [{ text: contentText } as Part]
      };
    });

  // Apply optimization to reduce token usage
  contents = optimizeHistory(contents);

  const currentParts: Part[] = [];
  let finalMessage = currentMessage;

  // ═══════════════════════════════════════════════════════════════════════════
  // TIERED SYSTEM INSTRUCTION — Replaces the old monolithic prompt assembly.
  // Reduces system prompt tokens by 50-90% based on query complexity.
  // ═══════════════════════════════════════════════════════════════════════════
  const tieredResult = buildTieredSystemInstruction(
    currentMessage,
    attachments.length > 0,
    validHistory.length,
    options,
    userCountry,
    isPremium,
    recentHistory,
    conversationSummary
  );

  let fullSystemInstruction = tieredResult.systemInstruction;

  // AI LEARNING FEEDBACK — Only inject for tier 3+ to save tokens on simple queries
  if (tieredResult.tier >= 3) {
    try {
      const detectedDomain = detectDomainFromMessage(currentMessage);
      const userId = user?.id || 'anonymous';

      const feedbackLearnings = await aiLearningService.getLearningsForPrompt(detectedDomain);
      if (feedbackLearnings) {
        fullSystemInstruction += `\n${feedbackLearnings}\n`;
      }

      if (userId !== 'anonymous') {
        const personalizedContext = await aiLearningService.getEnhancedPromptContext(userId, detectedDomain, currentMessage);
        if (personalizedContext) {
          fullSystemInstruction += `\n${personalizedContext}\n`;
        }
      }
    } catch (learningError) {
      console.warn('[Gemini] Learning system injection failed (non-critical):', learningError);
    }
  }

  // INTELLIGENT ATTACHMENT HANDLING
  let attachmentContext: EnhancedAttachmentContext | null = null;

  if (attachments.length > 0) {
    const needsPreAnalysis = shouldPreAnalyze(
      attachments.map(att => ({
        data: att.data,
        mimeType: att.mimeType,
        name: 'file',
        preview: ''
      })),
      currentMessage
    );

    if (needsPreAnalysis) {
      try {
        console.log('[Gemini] Pre-analyzing attachments for better context...');
        attachmentContext = await processAttachmentsIntelligently(
          attachments.map(att => ({
            data: att.data,
            mimeType: att.mimeType,
            name: 'file',
            preview: ''
          })),
          currentMessage
        );

        finalMessage = createOptimizedAttachmentPrompt(
          currentMessage,
          attachmentContext,
          isPremium ? 'accurate' : 'fast'
        );
      } catch (error) {
        console.error('[Gemini] Pre-analysis failed, using standard prompt:', error);
        finalMessage = `[Attached file(s). Analyze the content carefully.]\n\nUser Request: ${currentMessage}`;
      }
    } else {
      finalMessage = `[Attached file(s). Analyze the content carefully.]\n\n**Important:** Structure your response clearly with headers and sections if the answer is lengthy.\n\nUser Request: ${currentMessage}`;
    }
  } else {
    finalMessage = currentMessage;
  }

  if (finalMessage.trim()) currentParts.push({ text: finalMessage } as Part);
  attachments.forEach(att => {
    // PPTX files are not supported by Gemini API - convert to generic document type
    let mimeType = att.mimeType;
    if (mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
      mimeType === 'application/vnd.ms-powerpoint') {
      // Gemini doesn't support PPTX - send as image/png instead
      // The PPTX has already been base64 encoded, treat it as a document preview
      mimeType = 'image/png';
      console.log('[Gemini] Converting PPTX to image/png for API compatibility');
    }
    currentParts.push({ inlineData: { mimeType, data: att.data } });
  });
  contents.push({ role: 'user', parts: currentParts });

  // Model Escalation Logic - DISABLED: Always use the validated model
  // The AI should not auto-switch to different models
  let effectiveModel = validatedModel;
  // Model escalation disabled per user request - keep model consistent
  // if (allowModelEscalation) {
  //   if (isPremium) {
  //     const nextModel = getNextTierModel(validatedModel);
  //     if (nextModel) effectiveModel = nextModel;
  //   }
  //   if (requiresAdvancedModel(currentMessage)) {
  //     const nextModel = getNextTierModel(effectiveModel);
  //     if (nextModel) effectiveModel = nextModel;
  //   }
  // }

  const tokensMultiplier = getModelMultiplier(effectiveModel);
  console.log(`Using model (Stream): ${effectiveModel} (${tokensMultiplier}x token cost)`);

  try {
    // TIERED OUTPUT LIMITS — Scale max output tokens by complexity tier
    // Tier 1 (simple): 4K is plenty for greetings/short answers
    // Tier 2 (standard): 16K covers most CPA questions
    // Tier 3 (complex): 32K for multi-step computations
    // Tier 4 (expert): 65K for full exam/attachment analysis
    const maxTokensByTier: Record<PromptComplexityTier, number> = { 1: 4096, 2: 16384, 3: 32768, 4: 65536 };
    const maxTokens = maxTokensByTier[tieredResult.tier];

    const model = ai.getGenerativeModel({
      model: effectiveModel,
      systemInstruction: fullSystemInstruction,
      generationConfig: {
        temperature: 0.05, // Very low for maximum accuracy and consistency
        topK: 64, // Broader knowledge recall while maintaining quality
        topP: 0.90, // Tighter nucleus sampling for more focused responses
        maxOutputTokens: maxTokens, // Scaled by complexity tier
      }
    });

    // Use retry with exponential backoff
    const requestStartTime = performance.now();
    const result = await connectionManager.retryWithBackoff(async () => {
      const streamPromise = model.generateContentStream({ contents });
      return await Promise.race([streamPromise, timeoutPromise]) as any;
    }, {
      maxRetries: 2, // Retry twice for streaming
      baseDelay: 2000,
      maxDelay: 10000
    });

    // Record latency for adaptive timeout
    const requestLatency = performance.now() - requestStartTime;
    connectionManager.recordLatency(requestLatency);
    console.log(`[Gemini] Request completed in ${requestLatency.toFixed(0)}ms`);

    // Clear the connection timeout — streaming has started successfully
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    let accumulatedText = '';
    let accumulatedThought = '';
    let isCollectingThought = false;
    let lastFinishReason: string | null = null;

    // Repetition detection to prevent loops
    const recentLines: string[] = [];
    const maxRecentLines = 20;
    let consecutiveRepeats = 0;
    let lastLine = '';

    // Stream processing with buffering for split tags
    let buffer = '';

    for await (const chunk of result.stream) {
      // Only stop if user manually aborts
      if (signal?.aborted) {
        console.log("Stream aborted by user");
        break;
      }

      // Track finish reason from the API to detect cutoffs
      try {
        const candidates = (chunk as any).candidates;
        if (candidates?.[0]?.finishReason) {
          lastFinishReason = candidates[0].finishReason;
        }
      } catch (_) { /* ignore parsing errors */ }

      const chunkText = chunk.text();
      buffer += chunkText;

      // Process buffer
      let processedText = '';
      let processedThought = '';

      // Loop to process all complete tags in buffer
      while (true) {
        if (isCollectingThought) {
          // We are inside a thought tag, look for closing tag
          const closingIndex = buffer.indexOf('</thought>');
          if (closingIndex !== -1) {
            // Found closing tag
            accumulatedThought += buffer.substring(0, closingIndex);
            isCollectingThought = false;
            buffer = buffer.substring(closingIndex + 10); // 10 is length of </thought>
          } else {
            // No closing tag yet, keep everything in buffer (or move safe part to accumulatedThought)
            // To be safe against split closing tags (e.g. </thou), we keep the end of buffer
            // But for simplicity and since thoughts are usually not huge chunks at once, 
            // we can just move everything except the last few chars to accumulatedThought
            // However, simpler is to just keep it in buffer until closing tag or end of stream
            // BUT: if thought is long, buffer grows. 
            // Optimization: Move everything up to the last '<' to accumulatedThought if we are sure it's not part of closing tag
            // Actually, we need to look for partial closing tag `</thought>`
            // Partial could be `<`, `</`, `</t`, etc.

            // Safe approach: check if buffer ends with partial closing tag
            // If not, we can flush most of it.
            // For now, let's just keep it simple: flush nothing until closing tag found, 
            // UNLESS buffer gets too big (e.g. > 1000 chars), then we assume no closing tag is imminent 
            // and flush safe part. But split tag could be anywhere.

            // Better approach for thought collection:
            // We don't need to show thought in real-time to user (it's hidden), 
            // so we can just accumulate it.
            // But we need to detect when it ends so we can start showing text again.
            // So we just wait for </thought>.
            break;
          }
        } else {
          // We are in text mode, look for opening tag
          const openingIndex = buffer.indexOf('<thought>');
          if (openingIndex !== -1) {
            // Found opening tag
            accumulatedText += buffer.substring(0, openingIndex);
            processedText += buffer.substring(0, openingIndex); // For yielding
            isCollectingThought = true;
            buffer = buffer.substring(openingIndex + 9); // 9 is length of <thought>
          } else {
            // No complete opening tag. 
            // Check for partial opening tag at the end
            // Partial could be `<` or `<t` or `<th` ...

            // Regex to match partial <thought> at end of string
            // Matches <, <t, <th, <tho, <thou, <thoug, <though, <thought (but full is caught above)
            const partialMatch = buffer.match(/<(?:t(?:h(?:o(?:u(?:g(?:h(?:t)?)?)?)?)?)?)?$/);

            if (partialMatch) {
              // We have a partial tag at the end
              // Flush everything before it
              const safePart = buffer.substring(0, partialMatch.index);
              accumulatedText += safePart;
              processedText += safePart;
              buffer = buffer.substring(partialMatch.index!); // Keep partial tag in buffer
            } else {
              // No partial tag, flush everything
              accumulatedText += buffer;
              processedText += buffer;
              buffer = '';
            }
            break;
          }
        }
      }

      // Minimal delay for APK/Capacitor WebView compatibility — keep as low as possible for smoothness
      const isCapacitor = !!(globalThis as any).Capacitor;
      if (isCapacitor) {
        await new Promise(resolve => setTimeout(resolve, 8));
      }

      // Yield if we have new text or if we just want to update status
      yield {
        text: accumulatedText,
        thought: accumulatedThought,
        modelUsed: effectiveModel,
        tokensMultiplier: tokensMultiplier,
        tokensCharged: tokensCharged
      };
    }

    // Process any remaining buffer at end of stream
    if (buffer.length > 0) {
      if (isCollectingThought) {
        accumulatedThought += buffer;
      } else {
        accumulatedText += buffer;
      }
    }

    // Detect incomplete responses and append continuation notice
    const wasCutOff = detectCutOffResponse(accumulatedText, lastFinishReason);
    if (wasCutOff) {
      console.warn(`[Gemini] Response appears cut off. finishReason: ${lastFinishReason}`);
      accumulatedText += '\n\n---\n⚠️ *My response was cut short. Please reply **"continue"** and I\'ll pick up where I left off.*';
    }

    // Handle empty response: if AI returned nothing visible, provide a fallback
    if (!accumulatedText || accumulatedText.trim().length === 0) {
      if (accumulatedThought && accumulatedThought.trim().length > 0) {
        // AI only produced thought content but no visible response — try to summarize thought
        const thoughtSummary = accumulatedThought.substring(0, 500).trim();
        accumulatedText = `Based on my analysis:\n\n${thoughtSummary}${accumulatedThought.length > 500 ? '...' : ''}\n\n*If you need a more detailed answer, just ask me to elaborate!*`;
        console.warn('[Gemini] Empty visible response but had thought content. Extracted summary. Length:', accumulatedThought.length);
      } else {
        // Completely empty response — give a helpful nudge instead of an error
        accumulatedText = "Let me try that again — could you rephrase your question or add a bit more detail? Sometimes a small tweak helps me give you a better answer.";
        console.warn('[Gemini] Completely empty response from API');
      }
    }

    // Final yield with complete response
    yield {
      text: accumulatedText,
      thought: accumulatedThought,
      modelUsed: effectiveModel,
      tokensMultiplier: tokensMultiplier,
      tokensCharged: tokensCharged
    };

    // Collect training data from successful interactions (async, non-blocking)
    if (accumulatedText && accumulatedText.length > 50 && !wasCutOff) {
      const detectedDomain = detectDomainFromMessage(currentMessage);
      collectTrainingData(currentMessage, accumulatedText, {
        domain: detectedDomain,
        complexity: currentMessage.length > 300 ? 'complex' : currentMessage.length > 100 ? 'moderate' : 'simple',
        sessionId: undefined,
        userId: user?.id,
        modelUsed: effectiveModel
      }).catch(err => console.warn('[TrainingData] Collection failed (non-critical):', err));
    }

  } catch (error: any) {
    // Clear timeout to prevent dangling timers
    if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }

    console.error("Error streaming from Gemini:", error);
    let errorMessage = "Hmm, something went wrong on my end. Let me know if you'd like to try again — I'm ready!";

    // CENTRALIZED TOKEN SYSTEM: Refund tokens on error
    if (chargeSuccessful && user && updateUser && tokensCharged > 0) {
      await refundTokens(tokensCharged, 'error', user, updateUser);
      console.log(`[TokenSystem] Refunded ${tokensCharged} tokens due to error`);
    }

    if (error.message === 'REQUEST_TIMEOUT') {
      errorMessage = "That took a bit too long! This usually happens with large files or complex questions. Try:\n1. Using a smaller file (under 2MB)\n2. Breaking your question into smaller parts\n3. Asking without attachments first";
    } else if (error.message?.includes('429')) {
      errorMessage = "I'm getting a lot of questions right now! Give me a few seconds and try again.";
    } else if (error.message?.includes('quota')) {
      errorMessage = "I've reached my daily limit. I'll be back at full capacity soon — please try again later!";
    } else if (error.message?.includes('RESOURCE_EXHAUSTED')) {
      errorMessage = "That file is a bit too large for me to process. Could you try a smaller file or break it into parts?";
    }

    yield {
      text: errorMessage,
      thought: "Stream Error: " + (error.message || 'Unknown'),
      tokensCharged: 0 // No charge on error (refunded)
    };
  }
}

export const generateQuiz = async (
  topic: string,
  count: number = 5,
  difficulty: 'easy' | 'medium' | 'hard' = 'medium',
  type: 'multiple-choice' | 'true-false' | 'identification' | 'essay' | 'mixed' = 'multiple-choice',
  focus: 'theories' | 'computations' | 'mixed' = 'mixed',
  modelName: string = 'gemini-2.5-flash-lite',
  onThought?: (thought: string) => void,
  user?: User,
  updateUser?: (updates: Partial<User>) => void,
  attachedFileContent?: string,
  contextMessages?: string
): Promise<any> => {
  return requestQueue.add(async () => {
    // CENTRALIZED TOKEN SYSTEM: Charge for quiz generation
    let tokensCharged = 0;
    if (user && updateUser) {
      const { detectQuizComplexity } = await import('../utils/complexityDetector');
      const complexity = detectQuizComplexity(count, difficulty, focus);

      const tokenRequest: TokenChargeRequest = {
        operationType: 'quiz',
        complexity,
        model: modelName.includes('pro') ? 'pro' : 'flash',
        features: {},
        estimatedOutputTokens: count * 150, // Estimate per question
      };

      try {
        const chargeResult = await chargeTokens(tokenRequest, user, updateUser);
        if (!chargeResult.canProceed) {
          throw new Error(`Insufficient tokens: ${chargeResult.warnings.join('. ')}`);
        }
        tokensCharged = chargeResult.charged;
        console.log(`[TokenSystem] Charged ${tokensCharged} tokens for quiz generation`);
      } catch (error) {
        console.error('[TokenSystem] Failed to charge for quiz:', error);
        throw error;
      }
    }

    const ai = getClient();
    if (!ai) throw new Error("API Key missing");

    const model = ai.getGenerativeModel({
      model: modelName,
      generationConfig: {
        temperature: 0.15, // Low temperature for accurate, consistent quiz generation
        topK: 40,
        topP: 0.90,
      }
    });

    let typeInstruction = "";
    let isEssay = false;
    switch (type) {
      case 'true-false': typeInstruction = "Format as True/False questions."; break;
      case 'identification': typeInstruction = "Format as Identification questions (provide answer key)."; break;
      case 'essay':
        typeInstruction = "Format as ESSAY questions requiring written paragraph responses.";
        isEssay = true;
        break;
      default: typeInstruction = "Format as standard multiple-choice questions with 4 options."; break;
    }

    let focusInstruction = "";
    switch (focus) {
      case 'theories': focusInstruction = "Focus PURELY on theoretical concepts, definitions, and standards (PFRS/PAS/Law). No calculations."; break;
      case 'computations': focusInstruction = "Focus PURELY on computational problems. Require solving."; break;
      default: focusInstruction = "Mix theoretical concepts and computational problems."; break;
    }

    // Build context-aware prompt
    let contextSection = '';
    if (attachedFileContent) {
      contextSection = `
**📎 ATTACHED FILE CONTENT:**
The user has provided the following file content as the PRIMARY source for quiz questions:

${attachedFileContent.substring(0, 8000)}

**CRITICAL QUIZ GENERATION RULES:**
1. Generate 80% of questions DIRECTLY from the attached file content above
2. Ensure questions test understanding of the specific concepts in the file
3. Generate 20% BONUS questions that:
   - Expand on topics mentioned in the file
   - Test related concepts not explicitly covered
   - Connect file content to broader CPA topics
   - Apply file concepts to practical scenarios
4. Mark bonus questions with "[BONUS]" prefix
`;
    } else if (contextMessages) {
      contextSection = `
**💬 CONVERSATION CONTEXT:**
The following is the recent conversation discussing "${topic}":

${contextMessages.substring(0, 6000)}

**QUIZ GENERATION RULES:**
1. Generate questions based on the concepts discussed in the conversation
2. Include 20-30% bonus questions that expand beyond what was explicitly discussed
3. Test both recall and application of the conversation's key points
`;
    }

    const prompt = `You are a Philippine CPA Board Exam question writer with 20 years of experience.

TASK: Create a ${difficulty} difficulty quiz about: "${topic}"
COUNT: ${count} questions
TYPE: ${type} (${typeInstruction})
FOCUS: ${focus} (${focusInstruction})
${contextSection}

<thought>
Before generating questions, I must:
1. For each question, identify the EXACT legal basis, standard, or formula
2. Verify the correct answer is INDISPUTABLY correct under current Philippine law/standards
3. For computations: work through the FULL calculation to verify the answer
4. For each wrong option: identify the SPECIFIC misconception it tests
5. Ensure correct answers are distributed across A/B/C/D (not always the same letter)
6. Double-check: Could a knowledgeable reviewer dispute my correct answer? If yes, revise.
</thought>

CRITICAL ANSWER ACCURACY PROTOCOL:
- You MUST be 100% certain of every correct answer before including it
- For tax rates: Use rates under CREATE Law (RA 11534) and EOPT Law (RA 12023) as applicable
- For accounting standards: Use current PFRS/PAS as adopted in the Philippines
- For legal questions: Cite the specific article/section of the applicable code
- If you are NOT CERTAIN of a fact, DO NOT include it as a question — skip it and write a different question you ARE certain about
- For computations: Show your work inside <thought> tags and verify the answer before finalizing

EXPLANATION REQUIREMENTS (for EVERY option):
Each explanation must be 2-4 sentences and include:
1. The legal/standard basis (e.g., "Under Sec. 27(A) NIRC as amended by CREATE...", "Per PAS 16.30...")
2. For the CORRECT answer: Why it is correct with the specific provision
3. For WRONG answers: The specific misconception or error it represents

EXAMPLE GOOD EXPLANATION:
"Under Sec. 27(A) of the NIRC, as amended by CREATE Law (RA 11534), the RCIT rate is 25% effective July 1, 2020. Option A (30%) was the pre-CREATE rate. Option B (20%) applies only to domestic corporations with net taxable income ≤₱5M AND total assets ≤₱100M (excluding land). Option D (35%) is the highest individual income tax bracket, not applicable to corporations."

DIFFICULTY: ${difficulty.toUpperCase()}
${difficulty === 'easy' ? '- 70% recall, 30% application. Test fundamentals with clear distractors.' : ''}
${difficulty === 'medium' ? '- 40% application, 30% analysis, 30% recall. Include traps based on common misconceptions. Require careful reading.' : ''}
${difficulty === 'hard' ? '- 30% analysis, 40% complex application, 30% synthesis. Test exceptions, edge cases, multi-concept integration. Include misleading data. Use EXCEPT/NOT/LEAST likely phrasing.' : ''}

DISTRACTOR DESIGN:
- Each wrong answer must represent a REAL student mistake (not random nonsense)
- Include options that would be correct under DIFFERENT circumstances
- Vary correct answer position across A/B/C/D
- For computations: wrong options should be results of common calculation errors (forgetting adjustments, using wrong rates, etc.)

${isEssay ? `
**ESSAY QUESTION FORMAT:**
Return EXACTLY this JSON structure for ESSAY questions:
{
  "title": "Short Quiz Title",
  "isEssay": true,
  "questions": [
    {
      "id": "q1",
      "text": "Essay question requiring a detailed written response...",
      "topic": "Specific Subtopic",
      "type": "essay",
      "pointValue": 10,
      "rubric": {
        "excellent": "Complete and accurate answer with proper citations to standards...",
        "good": "Mostly accurate with minor omissions...",
        "fair": "Partially correct but missing key elements...",
        "poor": "Significant errors or incomplete understanding..."
      },
      "sampleAnswer": "A comprehensive model answer that demonstrates mastery of the topic, including: [key point 1], [key point 2], [relevant standards/citations], [practical application]...",
      "keyPoints": ["Point 1 that must be covered", "Point 2 that must be covered", "Point 3 for full credit"]
    }
  ]
}

**ESSAY REQUIREMENTS:**
- Questions should require 2-4 paragraph written responses
- Include clear grading rubric with 4 levels
- Provide comprehensive sample answers
- List 3-5 key points that should be addressed
- Test deep understanding, not just memorization
- Require application of concepts to scenarios
` : `
Return EXACTLY this JSON structure:
{
  "title": "Short Quiz Title",
  "questions": [
    {
      "id": "q1",
      "text": "Question text with clear, professional wording?",
      "topic": "Specific Subtopic",
      "options": [
        { "id": "a", "text": "Option A text", "isCorrect": false, "explanation": "Why wrong - what misconception does this represent?" },
        { "id": "b", "text": "Option B text", "isCorrect": true, "explanation": "Why correct - cite standard if applicable" },
        { "id": "c", "text": "Option C text", "isCorrect": false, "explanation": "Why wrong - common error this tests" },
        { "id": "d", "text": "Option D text", "isCorrect": false, "explanation": "Why wrong - what trap does this represent?" }
      ]
    }
  ]
}`}

Make every question count. This quiz should separate students who truly understand from those who just memorized.`;

    const result = await model.generateContentStream(prompt);

    let fullText = '';
    let thoughtText = '';

    for await (const chunk of result.stream) {
      const chunkText = chunk.text();
      fullText += chunkText;

      // Extract thought process
      if (fullText.includes('<thought>')) {
        const thoughtMatch = fullText.match(/<thought>([\s\S]*?)(?:<\/thought>|$)/);
        if (thoughtMatch) {
          const newThought = thoughtMatch[1].trim();
          if (newThought !== thoughtText) {
            thoughtText = newThought;
            if (onThought) onThought(thoughtText);
          }
        }
      }
    }

    // Clean potential markdown code blocks and thought tags
    // Use robust parser from aiResponseHandler
    const { robustParseJSON, validateQuizResponse } = await import('../utils/aiResponseHandler');

    // Retry mechanism for reliability - try up to 2 times with model escalation
    const MAX_RETRIES = 2;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          console.log(`[Quiz] Retrying generation (Attempt ${attempt + 1})...`);
          // On retry, use a more capable model
          const retryModelName = modelName.includes('lite') ? 'gemini-2.0-flash' : modelName;
          const retryModel = ai.getGenerativeModel({
            model: retryModelName,
            generationConfig: { temperature: 0.3 } // Lower temperature for more reliable output
          });

          // Simplified retry prompt
          const retryPrompt = `Generate a JSON quiz about "${topic}" with ${count} multiple-choice questions.
Return ONLY valid JSON in this format:
{
  "title": "Quiz about ${topic}",
  "questions": [
    {
      "id": "q1",
      "text": "Question text?",
      "topic": "${topic}",
      "options": [
        { "id": "a", "text": "Option A", "isCorrect": false, "explanation": "Why wrong" },
        { "id": "b", "text": "Option B", "isCorrect": true, "explanation": "Why correct" },
        { "id": "c", "text": "Option C", "isCorrect": false, "explanation": "Why wrong" },
        { "id": "d", "text": "Option D", "isCorrect": false, "explanation": "Why wrong" }
      ]
    }
  ]
}
NO markdown, NO code blocks, JUST valid JSON.`;

          const retryResult = await retryModel.generateContent(retryPrompt);
          fullText = retryResult.response.text();
        }

        const parsedData = robustParseJSON<any>(fullText);
        return validateQuizResponse(parsedData, count);
      } catch (e: any) {
        lastError = e;
        console.warn(`[Quiz] Attempt ${attempt + 1} failed:`, e.message);
        if (attempt < MAX_RETRIES - 1) {
          await new Promise(r => setTimeout(r, 1000)); // Wait 1 second before retry
        }
      }
    }

    // All attempts failed
    console.error("Quiz Generation Failed after all retries:", lastError);
    throw new Error("Failed to generate valid quiz data. Please try again or reduce the question count.");
  });
};

export interface MockExamOptions {
  questionFormat?: 'multiple_choice_only' | 'mixed_with_essays' | 'essays_only';
  essayCount?: number;
  essayConfig?: {
    includeRubric?: boolean;
    gradingScale?: '1.0-5.0';
    minWordCount?: number;
    includeKeyPoints?: boolean;
  };
}

export const generateMockExam = async (
  subject: string,
  itemCount: number = 50,
  difficulty: 'hard' | 'very_hard' | 'cpale_level' = 'hard',
  focus: 'theories' | 'computations' | 'mixed' = 'mixed',
  modelName: string = 'gemini-2.5-flash-lite',
  onThought?: (thought: string) => void,
  user?: User,
  updateUser?: (updates: Partial<User>) => void,
  essayOptions?: MockExamOptions
): Promise<any> => {
  // Enforce 100 item limit (increased from 75 due to batching capability)
  const safeItemCount = Math.min(itemCount, 100);

  // Essay configuration
  const questionFormat = essayOptions?.questionFormat || 'multiple_choice_only';
  const essayCount = essayOptions?.essayCount || 0;

  // Calculate MC vs Essay distribution
  const mcCount = questionFormat === 'essays_only' ? 0 :
    questionFormat === 'mixed_with_essays' ? Math.max(1, safeItemCount - essayCount) :
      safeItemCount;
  const actualEssayCount = questionFormat === 'essays_only' ? safeItemCount :
    questionFormat === 'mixed_with_essays' ? essayCount : 0;

  console.log(`[MockExam] Format: ${questionFormat}, MC: ${mcCount}, Essays: ${actualEssayCount}`);

  // TOKEN SYSTEM INTEGRATION
  let tokensCharged = 0;
  if (user && updateUser) {
    const { detectQuizComplexity } = await import('../utils/complexityDetector');
    const complexity = detectQuizComplexity(safeItemCount, difficulty, focus);

    // Estimate tokens: 200 per MC, 500 per Essay
    const estimatedTokens = (mcCount * 200) + (actualEssayCount * 500);

    const tokenRequest: TokenChargeRequest = {
      operationType: 'exam',
      complexity,
      model: modelName.includes('pro') ? 'pro' : 'flash',
      features: {},
      estimatedOutputTokens: estimatedTokens,
    };

    try {
      const chargeResult = await chargeTokens(tokenRequest, user, updateUser);
      if (!chargeResult.canProceed) {
        throw new Error(`Insufficient tokens: ${chargeResult.warnings.join('. ')}`);
      }
      tokensCharged = chargeResult.charged;
      console.log(`[TokenSystem] Charged ${tokensCharged} tokens for exam generation`);
    } catch (error) {
      console.error('[TokenSystem] Failed to charge for exam:', error);
      throw error;
    }
  }

  const ai = getClient();
  if (!ai) throw new Error("API Key missing");

  // Helper to generate a batch of questions
  const generateBatch = async (batchIndex: number, batchSize: number, isEssayBatch: boolean = false): Promise<any[]> => {
    // Retry logic for robust generation
    const MAX_RETRIES = 2;
    let lastError;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) console.log(`[MockExam] Retrying batch ${batchIndex + 1} (Attempt ${attempt + 1})...`);

        // Dynamic Model Selection for Retries
        const currentModelName = (attempt > 0 && modelName.includes('lite')) ? 'gemini-2.0-flash' : modelName;

        const model = ai.getGenerativeModel({
          model: currentModelName,
          generationConfig: {
            temperature: 0.1, // Very low for maximum answer accuracy
            topK: 40,
            topP: 0.90,
          }
        });

        // Prompt Construction for this Batch
        const batchPrompt = `You are a Philippine CPA Board Exam question writer. Generate BATCH #${batchIndex + 1} of a Mock Board Exam.

SUBJECT: ${subject}
COUNT: Exactly ${batchSize} questions
TYPE: ${isEssayBatch ? 'ESSAY' : 'Multiple Choice'}
DIFFICULTY: ${difficulty.toUpperCase()}
FOCUS: ${focus}

ANSWER ACCURACY PROTOCOL (MANDATORY):
- You MUST be 100% certain of every correct answer. If unsure, write a different question.
- For tax: Use CREATE Law (RA 11534) and EOPT Law (RA 12023) rates and provisions.
- For accounting: Use current PFRS/PAS as adopted in the Philippines.
- For law: Cite specific articles/sections of the applicable code.
- For computations: Work through the calculation in your reasoning first, then verify.
- NEVER guess a rate, threshold, or deadline. Only use facts you are certain about.

${isEssayBatch ? `
Return a valid JSON array of essay question objects:
[
  {
    "id": "e_${batchIndex}_1",
    "text": "Detailed scenario-based essay question requiring analysis and application...",
    "topic": "Specific Subtopic",
    "type": "essay",
    "pointValue": 10,
    "sampleAnswer": "Comprehensive model answer with citations to specific standards/laws, step-by-step analysis, and professional conclusion. Must be 200+ words.",
    "keyPoints": ["Key concept 1 with standard reference", "Key concept 2", "Key concept 3", "Application point"]
  }
]

ESSAY QUALITY: Each sample answer must cite specific standards/laws, show analytical reasoning, and be comprehensive enough to serve as a model answer.
` : `
Return a valid JSON array of MCQ objects (NO markdown, NO code blocks, JUST the JSON array):
[
  {
    "id": "mc_${batchIndex}_1",
    "text": "Clear, professionally-worded question?",
    "topic": "Specific Subtopic",
    "options": [
      { "id": "a", "text": "Option A", "isCorrect": false, "explanation": "Why wrong: cite the specific misconception and correct rule (2-3 sentences)" },
      { "id": "b", "text": "Option B", "isCorrect": true, "explanation": "Why correct: cite the specific standard/law provision (2-3 sentences)" },
      { "id": "c", "text": "Option C", "isCorrect": false, "explanation": "Why wrong: what error does this represent (2-3 sentences)" },
      { "id": "d", "text": "Option D", "isCorrect": false, "explanation": "Why wrong: under what circumstances would this be correct (2-3 sentences)" }
    ]
  }
]

EXPLANATION QUALITY: Every explanation must be 2-3 sentences with a legal/standard citation. Never write one-sentence explanations.
DISTRACTOR QUALITY: Each wrong answer must represent a real student mistake, not random text. Vary correct answer positions across A/B/C/D.
`}

OUTPUT: Pure JSON array only. No markdown, no code blocks, no commentary.`;

        const result = await model.generateContent(batchPrompt);
        const responseText = result.response.text();

        // Robust Parsing
        const { robustParseJSON } = await import('../utils/aiResponseHandler');
        const parsedBatch = robustParseJSON<any[]>(responseText);

        if (!Array.isArray(parsedBatch)) {
          throw new Error("Batch response is not an array");
        }

        const validItems = parsedBatch.filter(item => {
          return item && item.text && (isEssayBatch || (item.options && item.options.length >= 2));
        });

        if (validItems.length === 0) {
          throw new Error("No valid items in batch");
        }

        return validItems;

      } catch (err: any) {
        lastError = err;
        console.warn(`[MockExam] Batch ${batchIndex + 1} failed:`, err.message);
        await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
      }
    }

    console.error(`[MockExam] Batch ${batchIndex + 1} failed permanently.`);
    return [];
  };

  // Execution Strategy
  const batches: (() => Promise<any[]>)[] = [];
  const BATCH_SIZE_MC = 10;
  const BATCH_SIZE_ESSAY = 2;

  // 1. Plan MC Batches
  if (mcCount > 0) {
    const numMcBatches = Math.ceil(mcCount / BATCH_SIZE_MC);
    for (let i = 0; i < numMcBatches; i++) {
      const count = Math.min(BATCH_SIZE_MC, mcCount - (i * BATCH_SIZE_MC));
      batches.push(() => generateBatch(i, count, false));
    }
  }

  // 2. Plan Essay Batches
  if (actualEssayCount > 0) {
    const numEssayBatches = Math.ceil(actualEssayCount / BATCH_SIZE_ESSAY);
    const startIdx = batches.length;
    for (let i = 0; i < numEssayBatches; i++) {
      const count = Math.min(BATCH_SIZE_ESSAY, actualEssayCount - (i * BATCH_SIZE_ESSAY));
      batches.push(() => generateBatch(startIdx + i, count, true));
    }
  }

  if (onThought) onThought("Planning exam strategy and generating questions in parallel batches...");

  const updateProgress = (completed: number, total: number) => {
    if (onThought) onThought(`Generating exam... (${Math.round((completed / total) * 100)}% complete)`);
  };

  let completedBatches = 0;

  const batchPromises = batches.map(async (batchFn, idx) => {
    // Stagger batch starts to avoid rate limiting (reduced from 1500ms for faster generation)
    await new Promise(r => setTimeout(r, idx * 800));
    // Execute batch directly without nested queue (avoids deadlock)
    const result = await batchFn();
    completedBatches++;
    updateProgress(completedBatches, batches.length);
    return result;
  });

  const results = await Promise.all(batchPromises);

  // 4. Aggregate Results
  const allQuestions = results.flat();

  // 5. Final Structure Assembly
  const { validateQuizResponse } = await import('../utils/aiResponseHandler');

  const mockExamData = {
    title: `Mock Exam: ${subject}`,
    isEssay: questionFormat === 'essays_only',
    questions: allQuestions
  };

  try {
    return validateQuizResponse(mockExamData, safeItemCount);
  } catch (e: any) {
    console.error("Mock Exam Assembly Failed:", e);
    throw new Error(`Failed to assemble mock exam. Generated ${allQuestions.length} valid questions.`);
  }
};

export const sendMessageToGemini = async (
  history: Message[],
  currentMessage: string,
  attachments: { mimeType: string; data: string }[] = [],
  modelName: string = 'gemini-2.5-flash-lite',
  isPremium: boolean = false,
  allowModelEscalation: boolean = true,
  options: AIOptions = {},
  userCountry: string = 'Philippines'
): Promise<{ text: string; thought?: string; modelUsed?: string; tokensMultiplier?: number }> => {
  // Check rate limits before proceeding
  const estimatedTokens = Math.ceil(currentMessage.length / 4) + (attachments.length * 1000);
  const rateCheck = rateLimiter.canMakeRequest(estimatedTokens);

  if (!rateCheck.allowed) {
    console.warn('[Gemini] Rate limited:', rateCheck.reason);
    return {
      text: `⚠️ **Rate Limit**: ${rateCheck.reason}\n\nPlease wait ${Math.ceil((rateCheck.waitTime || 0) / 1000)} seconds before trying again.`,
      thought: "Rate limit protection triggered"
    };
  }

  // Wrap the entire execution in the request queue
  return requestQueue.add(async () => {
    // Record request for rate limiting
    rateLimiter.recordRequest(estimatedTokens);
    // 1. Check Cache
    const cacheKey = responseCache.generateKey([...history, { role: 'user', content: currentMessage }], modelName);
    const cached = responseCache.get(cacheKey);
    if (cached) {
      console.log("Serving from cache:", cacheKey);
      return cached;
    }

    // Validate model name - convert invalid/preview models to default
    const VALID_MODELS_LIST = MODEL_HIERARCHY.map(m => m.name);
    const FALLBACK_MODEL = 'gemini-2.5-flash-lite';

    let validatedModel = modelName;
    if (!modelName || modelName.includes('preview') || modelName.includes('exp') || !VALID_MODELS_LIST.includes(modelName)) {
      console.warn(`[Gemini] Invalid model "${modelName}" → using ${FALLBACK_MODEL}`);
      validatedModel = FALLBACK_MODEL;
    }

    console.log(`Starting AI request - Model: ${validatedModel}, Premium: ${isPremium}, Options:`, options);
    const ai = getClient();

    if (!ai) {
      return {
        text: "⚠️ **System Error**: API Key is missing. Please configure your Gemini API Key in the Admin Dashboard → Settings. You can get a free API key from [Google AI Studio](https://aistudio.google.com/apikey).",
        thought: "Configuration Error: API Key is not configured in admin settings."
      };
    }

    // Context Management: Dynamic history length
    // MOBILE OPTIMIZATION: Reduce history significantly for mobile to save data
    const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isCapacitorApp = !!(window as any).Capacitor;

    // Mobile gets reduced context to save data and battery
    let MAX_HISTORY: number;
    if (isMobileDevice || isCapacitorApp) {
      MAX_HISTORY = isPremium ? 8 : 5; // Reduced for mobile
    } else {
      MAX_HISTORY = isPremium ? 20 : 10; // Desktop gets full context
    }

    const recentHistory = history.slice(-MAX_HISTORY);

    // Convert internal Message format to Gemini Content format
    let contents: Content[] = recentHistory
      .filter(msg => msg.role !== Role.SYSTEM && !msg.isError)
      .map(msg => {
        // MOBILE OPTIMIZATION: Truncate old messages to save bandwidth
        let contentText = msg.content;

        // For mobile, truncate messages older than last 2 to save data
        const isRecent = recentHistory.indexOf(msg) >= recentHistory.length - 2;
        if ((isMobileDevice || isCapacitorApp) && !isRecent && contentText.length > 500) {
          contentText = contentText.substring(0, 500) + '...[truncated for mobile]';
        }

        // Include attachment metadata in history for context continuity
        if (msg.attachments && msg.attachments.length > 0) {
          const attachmentInfo = msg.attachments.map(att => `[Attached: ${att.name}]`).join(' ');
          contentText = `${attachmentInfo}\n${contentText}`;
        }
        return {
          role: msg.role === Role.USER ? 'user' : 'model',
          parts: [{ text: contentText } as Part]
        };
      });

    // Apply Optimization
    contents = optimizeHistory(contents);

    const currentParts: Part[] = [];

    // ═══════════════════════════════════════════════════════════════════════════
    // TIERED SYSTEM INSTRUCTION — Same optimization as streaming function.
    // ═══════════════════════════════════════════════════════════════════════════
    const validHistory2 = recentHistory.filter(msg => msg.role !== Role.SYSTEM && !msg.isError);
    const tieredResult2 = buildTieredSystemInstruction(
      currentMessage,
      attachments.length > 0,
      validHistory2.length,
      options,
      userCountry,
      isPremium,
      recentHistory,
      '' // No conversation summary for non-streaming (simpler context)
    );

    const localizedSystemInstruction = tieredResult2.systemInstruction;

    // Refined prompt for better accuracy and conciseness
    let finalMessage = currentMessage;
    if (attachments.length > 0) {
      finalMessage = `[System Instruction: The user has attached a file/image. 
        1. Analyze the image/document carefully.
        2. Provide a concise summary (1-2 sentences) of what you see.
        3. Answer the specific user request below accurately but concisely. Avoid verbose filler.]
        
        User Request: ${currentMessage}`;
    } else {
      finalMessage = `${currentMessage} (Please be concise.)`;
    }

    if (finalMessage.trim()) {
      currentParts.push({ text: finalMessage } as Part);
    }

    attachments.forEach(att => {
      // PPTX files are not supported by Gemini API - convert to generic document type
      let mimeType = att.mimeType;
      if (mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
        mimeType === 'application/vnd.ms-powerpoint') {
        // Gemini doesn't support PPTX - send as image/png instead
        mimeType = 'image/png';
        console.log('[Gemini] Converting PPTX to image/png for API compatibility (non-streaming)');
      }
      currentParts.push({
        inlineData: {
          mimeType,
          data: att.data
        }
      });
    });

    contents.push({
      role: 'user',
      parts: currentParts
    });

    // Check if we should escalate to a better model - start from validated model
    let effectiveModel = validatedModel;
    let tokensMultiplier = 1;

    if (allowModelEscalation) {
      // 1. Premium User Base Upgrade: Always start with at least Gemini 2.5 Flash
      if (isPremium) {
        if (effectiveModel.includes('lite')) {
          effectiveModel = 'gemini-2.5-flash';
          tokensMultiplier = 1.5;
          console.log('[Gemini] Premium User: Auto-upgraded from Lite to Gemini 2.5 Flash');
        }
      }

      // 2. Complexity Escalation: Upgrade further if problem is complex
      if (requiresAdvancedModel(currentMessage)) {
        const nextModel = getNextTierModel(effectiveModel);
        if (nextModel) {
          effectiveModel = nextModel;
          console.log(`Complex problem detected - Escalating to ${effectiveModel}`);
        }
      }
    }

    tokensMultiplier = getModelMultiplier(effectiveModel);
    console.log(`Using model: ${effectiveModel} (${tokensMultiplier}x token cost)`);

    try {
      // MAXIMUM OUTPUT - No artificial limits
      const maxTokens = 65536;

      const model = ai.getGenerativeModel({
        model: effectiveModel,
        systemInstruction: localizedSystemInstruction,
        generationConfig: {
          temperature: 0.1,
          topK: 40,
          topP: 0.95,
          maxOutputTokens: maxTokens, // Let AI complete fully
        }
      });

      // Adaptive timeout
      const TIMEOUT_MS = connectionManager.getAdaptiveTimeout(isPremium);
      console.log(`[Gemini] Using adaptive timeout: ${TIMEOUT_MS}ms`);

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('REQUEST_TIMEOUT')), TIMEOUT_MS);
      });

      // Use retry with exponential backoff
      const requestStartTime = performance.now();

      let response;
      try {
        response = await connectionManager.retryWithBackoff(async () => {
          const contentPromise = model.generateContent({ contents });
          const result = await Promise.race([contentPromise, timeoutPromise]) as any;

          // Critical: Check for "soft refusals" (AI saying "I can't") and treat as errors to trigger escalation
          try {
            const candidateText = result.response?.text();
            if (candidateText) {
              const refusalRegex = /^(I cannot|I am unable|I apologize|I'm sorry|I am not sure|I don't know|As an AI)/i;
              if (refusalRegex.test(candidateText) || (candidateText.length < 100 && /cannot|unable|apologize/i.test(candidateText))) {
                console.warn('[Gemini] Soft refusal detected. Treating as error to trigger retry/escalation.');
                throw new Error('AI_REFUSAL_DETECTED');
              }
            }
          } catch (e) {
            if ((e as Error).message === 'AI_REFUSAL_DETECTED') throw e;
            // Ignore other parsing errors at this stage
          }

          return result;
        }, {
          maxRetries: 3, // More retries for non-streaming
          baseDelay: 1000,
          maxDelay: 15000
        });
      } catch (error) {
        // ESCALATION LOGIC:
        // Premium -> God Mode (Gemini 3 Flash Preview)
        // Standard -> Advanced (Gemini 2.5 Flash) if using Lite

        let escalationModel = null;
        let escalationName = '';

        if (isPremium) {
          // Premium User: Always try God Mode if not already there
          if (effectiveModel !== 'gemini-3-flash-preview') {
            escalationModel = 'gemini-3-flash-preview';
            escalationName = 'GOD MODE (Gemini 3 Flash Preview)';
          }
        } else {
          // Standard User: Escalate from Lite to Full Flash
          // Only escalate if we were using a Lite model
          if (effectiveModel.includes('lite') && effectiveModel !== 'gemini-2.5-flash') {
            escalationModel = 'gemini-2.5-flash';
            escalationName = 'Advanced Mode (Gemini 2.5 Flash)';
          }
        }

        if (escalationModel) {
          console.log(`⚠️ Standard attempt failed. Escalating to ${escalationName}...`);

          try {
            const retryModel = ai.getGenerativeModel({
              model: escalationModel,
              systemInstruction: localizedSystemInstruction,
              generationConfig: {
                temperature: 0.2,
                topK: 40,
                topP: 0.95,
                maxOutputTokens: maxTokens,
              }
            });

            // Extended timeout for escalation
            const ESCALATION_TIMEOUT = 60000;
            const retryTimeoutPromise = new Promise<never>((_, reject) => {
              setTimeout(() => reject(new Error('ESCALATION_TIMEOUT')), ESCALATION_TIMEOUT);
            });

            response = await Promise.race([
              retryModel.generateContent({ contents }),
              retryTimeoutPromise
            ]);

            effectiveModel = escalationModel;
            // Update multiplier based on new model tier
            tokensMultiplier = isPremium ? 2.0 : 1.5;
            console.log(`✅ ESCALATION SUCCESS: Recovered using ${escalationName}`);
          } catch (retryError) {
            console.error(`❌ ESCALATION FAILED (${escalationName}):`, retryError);
            throw error; // Throw original error if escalation fails
          }
        } else {
          throw error; // No escalation context applies
        }
      }

      // Record latency
      const requestLatency = performance.now() - requestStartTime;
      connectionManager.recordLatency(requestLatency);
      console.log(`[Gemini] Request completed in ${requestLatency.toFixed(0)}ms`);

      const result = response.response;
      let fullText = result.text() || "I analyzed your input but couldn't generate a text response. Please try again.";
      let thoughtProcess = '';

      const parts = result.candidates?.[0]?.content?.parts;

      // Manual extraction if the model outputs XML tags for thoughts (fallback)
      const thoughtMatch = fullText.match(/<thought>([\s\S]*?)<\/thought>/);
      if (thoughtMatch) {
        thoughtProcess = thoughtMatch[1].trim();
        fullText = fullText.replace(/<thought>[\s\S]*?<\/thought>/, '').trim();
      }
      // If the API returns separate parts (standard for some thinking models)
      else if (parts && parts.length > 1) {
        const firstPartText = parts[0].text;
        const secondPartText = parts[1].text;

        if (firstPartText && secondPartText) {
          thoughtProcess = firstPartText;
          fullText = secondPartText;
        }
      }

      // Sanitize the response before returning
      const { sanitizeAIResponse } = await import('../utils/responseSanitizer');
      const { sanitized, wasModified, issues } = sanitizeAIResponse(fullText);

      if (wasModified) {
        console.log('[Gemini] Non-streaming response sanitized:', issues);
        fullText = sanitized;
      }

      const output = {
        text: fullText,
        thought: thoughtProcess,
        modelUsed: effectiveModel,
        tokensMultiplier: tokensMultiplier
      };

      // 2. Store in Cache
      responseCache.set(cacheKey, output);

      return output;

    } catch (error: any) {
      console.error("Error calling Gemini API:", error);

      let errorMessage = "Hmm, something went wrong on my end. Let me know if you'd like to try again — I'm ready!";
      let thoughtError = "API Error";

      if (error.message === 'REQUEST_TIMEOUT') {
        errorMessage = "That took a bit too long! This usually happens with large files or complex questions. Try:\n1. Using a smaller file (under 2MB)\n2. Breaking your question into smaller parts\n3. Asking without attachments first";
        thoughtError = "Request Timeout";
      } else if (error.message?.includes('429')) {
        errorMessage = "I'm getting a lot of questions right now! Give me a few seconds and try again.";
        thoughtError = "Resource Exhausted (429) - Free Tier Pause";
      } else if (error.message?.includes('quota')) {
        errorMessage = "I've reached my daily limit. I'll be back at full capacity soon — please try again later!";
        thoughtError = "Quota Exceeded";
      } else if (error.message?.includes('RESOURCE_EXHAUSTED')) {
        errorMessage = "That file is a bit too large for me to process. Could you try a smaller file or break it into parts?";
        thoughtError = "Resource Exhausted";
      } else if (error.message?.includes('API key')) {
        errorMessage = "There's a configuration issue on our end. Please try again shortly.";
        thoughtError = "Invalid API Key";
      } else if (error.message?.includes('DEADLINE_EXCEEDED')) {
        errorMessage = "That took a bit too long! Try a smaller file or a simpler question.";
        thoughtError = "Deadline Exceeded";
      }

      return {
        text: errorMessage,
        thought: thoughtError
      };
    }
  });
};
